import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import { NodevarList } from '../../components/nodevar-list'
import { mcpCodeValidation } from '../../config/mcp-tool-validation'
import { LockedCustomCodeAction } from '../ai-agents/locked-custom-code-action'
import { McpCodeEditor, McpFunctionSchemaEditor } from './mcp-tool-code-editor'
import { normalizeCodeName } from './mcp-tool-config'
import { type McpToolFormValues, resetCustomCodeInputs } from './mcp-tool-inputs'

interface McpCustomCodeFieldsProps {
  form: GenericFormType<McpToolFormValues>
  nodeVars: string[]
}

export const McpCustomCodeFields = ({ form, nodeVars }: McpCustomCodeFieldsProps) => {
  const name = form.useStore((state) => state.values.code_name) ?? ''
  const description = form.useStore((state) => state.values.code_description) ?? ''
  if (isEmbedMode()) return <LockedCustomCodeAction name={name} description={description} />

  const setSchema = (schema: unknown) => {
    resetCustomCodeInputs(form, schema)
    form.setFieldValue('code_function_schema', schema)
    form.validateField('code_function_schema', 'change')
  }
  const fields: GenericFieldConfig<McpToolFormValues> = [
    {
      name: 'code_name',
      componentType: 'input',
      label: 'Function name',
      zodValidation: mcpCodeValidation.code_name,
      eventDataExtractor: (event) => normalizeCodeName(event.target.value),
      inputProps: { placeholder: 'Enter function name', 'data-testid': 'input-function-name' },
    },
    {
      name: 'code_description',
      componentType: 'textarea',
      zodValidation: mcpCodeValidation.code_description,
      inputProps: {
        label: 'Function description',
        placeholder: 'Describe what this function does and when to use it.',
        className: 'min-h-[120px] bg-background',
        'data-testid': 'textarea-function-description',
        endIcon: (
          <NodevarList nodevars={nodeVars} inputElementSelector='[name="code_description"]' />
        ),
      },
    },
    {
      name: 'code_body',
      type: 'custom',
      eventDataExtractor: (value) => value,
      zodValidation: mcpCodeValidation.code_body,
      component: ({ value, onChange }) => (
        <McpCodeEditor
          code={(value as string) || ''}
          name={name}
          description={description}
          onChange={onChange}
          onSchema={setSchema}
          onApply={(code, schema, generatedName, generatedDescription) => {
            form.setFieldValue('code_body', code)
            form.setFieldValue('code_name', normalizeCodeName(generatedName))
            form.setFieldValue('code_description', generatedDescription)
            setSchema(schema)
            form.validateAllFields('change')
          }}
        />
      ),
    },
    {
      name: 'code_function_schema',
      type: 'custom',
      eventDataExtractor: (value) => value,
      zodValidation: mcpCodeValidation.code_function_schema,
      component: ({ value, onChange }) => (
        <McpFunctionSchemaEditor
          value={value}
          onChange={(schema) => {
            resetCustomCodeInputs(form, schema)
            onChange(schema)
          }}
        />
      ),
    },
  ]
  return (
    <div className="col-span-2 flex flex-col gap-4">
      <FieldsRenderer form={form} fields={fields} />
    </div>
  )
}
